import { css } from '@emotion/react'
import React from 'react'
import { PolymorphicPropsWithoutRef } from 'react-polymorphic-types'

import Icon from 'components/Icon'
import NumberInput from 'components/NumberInput'
import theme from 'theme'

const InputDefaultElement = 'input'

export interface InputOwnProps {
  className?: string
  inputClassName?: string
  isInvalid?: boolean
  rightAligned?: boolean
  search?: boolean
  inputRef?: React.Ref<HTMLInputElement>
  inputStyleOverrides?: Record<string, any> | Array<any>
  suffix?: React.ReactNode
  wrapSuffix?: boolean
  prefix?: React.ReactNode
  wrapPrefix?: boolean
  disabled?: boolean
  disableFocusIndicator?: boolean
}

export type InputProps<
  T extends React.ElementType = typeof InputDefaultElement
> = PolymorphicPropsWithoutRef<InputOwnProps, T>

const Input: React.FC<InputProps> = ({
  className,
  inputClassName,
  rightAligned,
  search,
  isInvalid,
  inputRef,
  suffix,
  wrapSuffix,
  prefix,
  wrapPrefix,
  disabled,
  disableFocusIndicator,
  inputStyleOverrides,
  as,
  type,
  ...rest
}: InputProps) => {
  // if it's a text input, use our NumberInput so we avoid the mouse wheel issue
  const DefaultComponent = type === 'number' ? NumberInput : 'input'
  // polymorphic component overrides type
  const InputComponent = as ? as : DefaultComponent

  return (
    <>
      {search && (
        <Icon
          css={{
            color: theme.colors.steel[1],
            marginLeft: theme.space[3],
            fontSize: theme.fontSizes[2],
            position: 'absolute',
            top: wrapSuffix ? '18px' : 'auto'
          }}
          icon="search"
        />
      )}
      <InputWrapper
        className={className}
        disabled={disabled}
        disableFocusIndicator={disableFocusIndicator}
        isInvalid={isInvalid}
        isPrefix={Boolean(prefix) || null}
        wrapPrefix={wrapPrefix}
        wrapSuffix={wrapSuffix}
      >
        {Boolean(prefix) && prefix}
        <InputComponent
          ref={inputRef}
          className={inputClassName}
          css={inputStyles({
            rightAligned,
            search,
            overrides: inputStyleOverrides
          })}
          disabled={disabled}
          type={type}
          {...rest}
        />
        {Boolean(suffix) && suffix}
      </InputWrapper>
    </>
  )
}

export default Input

export const inputStyles: (props: Record<string, any>) => any = ({
  rightAligned = false,
  search = false,
  overrides = {}
}) =>
  css(
    {
      width: '100%',
      maxWidth: '100%',
      height: 48,
      border: 0,
      background: 'none',
      display: 'block',
      minWidth: 0,
      color: theme.colors.steel[2],
      paddingLeft: search ? theme.space[6] : theme.space[3],
      paddingRight: theme.space[3],
      fontSize: theme.fontSizes[2],
      fontWeight: theme.fontWeights[0],
      borderRadius: theme.borderRadius[0],

      '&::placeholder': {
        color: theme.colors.steel[1],
        fontWeight: theme.fontWeights[0],
        opacity: theme.opacity[0]
      },

      '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
        {
          transition: 'all 5000s ease-in-out 0s',
          transitionProperty: 'background-color, color'
        }
    },

    rightAligned && {
      textAlign: 'right'
    },

    overrides
  )

type InputWrapperProps = {
  wrapSuffix?: boolean
  wrapPrefix?: boolean
  isPrefix?: boolean
  disabled?: boolean
  disableFocusIndicator?: boolean
  isInvalid?: boolean
  className?: string
}

export const InputWrapper: React.FC<InputWrapperProps> = ({
  wrapSuffix,
  wrapPrefix,
  isPrefix,
  disabled,
  disableFocusIndicator,
  isInvalid,
  ...rest
}) => (
  <div
    css={[
      {
        display: wrapSuffix || wrapPrefix ? 'block' : 'flex',
        borderRadius: isPrefix
          ? `0 ${theme.borderRadius[0]}px ${theme.borderRadius[0]}px 0`
          : theme.borderRadius[0],
        border: theme.border.base,
        background: 'white',
        alignItems: 'center',
        transition: '150ms border-color',
        width: '100%',
        minHeight: 48,
        '&:focus-within': {
          borderColor:
            !disableFocusIndicator && !isInvalid && theme.colors.blue[0]
        }
      },

      disabled && {
        opacity: theme.opacity[0]
      },

      isInvalid && {
        borderColor: theme.colors.red[0]
      }
    ]}
    {...rest}
  />
)

type SuffixProps = {
  disabled?: boolean
}

export const Suffix: React.FC<SuffixProps> = ({ disabled, ...rest }) => {
  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        fontSize: '18px',
        fontWeight: theme.fontWeights[0],
        color: !disabled && theme.colors.blue[0],
        borderLeft: theme.border.base,
        borderRadius: `0 ${theme.borderRadius[0]}px ${theme.borderRadius[0]}px 0`,
        minWidth: 48,
        minHeight: 48,
        justifyContent: 'center'
      }}
      {...rest}
    />
  )
}

type PrefixProps = {
  disabled?: boolean
}

export const Prefix: React.FC<PrefixProps> = ({ disabled, ...rest }) => {
  return (
    <div
      css={{
        display: 'flex',
        flexShrink: 1,
        alignItems: 'center',
        fontSize: theme.fontSizes[2],
        fontWeight: theme.fontWeights[0],
        color: !disabled && theme.colors.blue[0],
        backgroundColor: theme.colors.panel[0],
        border: theme.border.base,
        borderRadius: `${theme.borderRadius[0]}px 0 0 ${theme.borderRadius[0]}px`,
        paddingLeft: theme.space[3],
        paddingRight: theme.space[3],
        minWidth: 48,
        minHeight: 48,
        justifyContent: 'center',
        marginBottom: theme.space[4]
      }}
      {...rest}
    />
  )
}
